import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from '@/lib/api';

export type NotificationType = 'success' | 'error' | 'info';

const createNotificationId = (): string => {
  const cryptoRef = globalThis?.crypto;
  if (cryptoRef?.randomUUID) {
    return cryptoRef.randomUUID();
  }
  if (cryptoRef?.getRandomValues) {
    const bytes = cryptoRef.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `notif-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  timestamp: number;
  read: boolean;
  link?: string;
  metadata?: {
    prompt?: string;
    generationId?: string;
  };
}

interface NotificationState {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  removeNotification: (id: string) => void;
  unreadCount: () => number;
  syncWithBackend: () => Promise<void>; // New sync method
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      
      addNotification: (notification) => {
        set((state) => {
          // Dedupe by generationId + type within the last 5 minutes. The
          // title used to be part of the dedupe key, but the backend's
          // synced "Готово / Изображение готово" message and the
          // frontend's "Видео готово" message both report the SAME
          // completion event — different wording, same generationId.
          // Match on type (success vs error) so a later failure event
          // for the same gen still comes through.
          const generationId = notification.metadata?.generationId;
          if (generationId) {
            const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
            const duplicate = state.notifications.some(
              (n) => {
                if (n.timestamp < fiveMinutesAgo) return false;
                if (n.type !== notification.type) return false;
                const m = n.metadata as Record<string, unknown> | undefined;
                return m?.generationId === generationId
                    || m?.generation_id === generationId;
              }
            );
            if (duplicate) return state;
          }
          return {
            notifications: [
              {
                id: createNotificationId(),
                timestamp: Date.now(),
                read: false,
                ...notification,
              },
              ...state.notifications,
            ].slice(0, 50), // Keep last 50 notifications
          };
        });
      },

      syncWithBackend: async () => {
          try {
              const apiNotifs = await fetchNotifications(20);
              
              set((state) => {
                  // Merge strategy:
                  // 1. Convert API notifications to store format
                  // 2. Add only ones that don't exist by ID (assuming backend IDs are UUIDs)
                  // 3. Update read status if backend says read
                  
                  const newNotifications = [...state.notifications];
                  let changed = false;

                  apiNotifs.forEach(apiN => {
                      const exists = newNotifications.find(n => n.id === apiN.id);
                      if (!exists) {
                          // Cross-source dedupe: the per-component poller in
                          // useGenerationPolling already adds a local
                          // notification with title like "Видео готово" the
                          // moment a generation completes. The backend
                          // mirrors the same event with a generic
                          // "Готово / Изображение готово" body — different
                          // wording but same generationId. Without this
                          // check the bell ends up with two entries per
                          // gen (one client-side, one synced from backend).
                          const apiGenId = (apiN.meta as Record<string, unknown> | undefined)?.generation_id
                              ?? (apiN.meta as Record<string, unknown> | undefined)?.generationId;
                          if (apiGenId) {
                              const dup = newNotifications.find(n => {
                                  const m = n.metadata as Record<string, unknown> | undefined;
                                  return (m?.generationId === apiGenId || m?.generation_id === apiGenId);
                              });
                              if (dup) {
                                  // Sync read state across the dup if needed and skip insert.
                                  if (apiN.is_read && !dup.read) {
                                      dup.read = true;
                                      changed = true;
                                  }
                                  return;
                              }
                          }

                          // Map backend type to UI dot color. Anything that
                          // adds credits or completes work cleanly = green;
                          // anything that drains credits / fails = red;
                          // everything else stays neutral blue.
                          let type: NotificationType = 'info';
                          const positiveTypes = new Set([
                            'success',
                            'bonus',
                            'payment_succeeded',
                            'subscription_renewed',
                            'generation_completed',
                          ]);
                          const negativeTypes = new Set([
                            'error',
                            'admin_deduction',
                            'generation_failed',
                            'subscription_failed',
                          ]);
                          if (positiveTypes.has(apiN.type)) type = 'success';
                          else if (negativeTypes.has(apiN.type)) type = 'error';
                          else if (apiN.type === 'credits_adjustment') {
                            // Positive amount = grant (green), negative = deduction (red).
                            const amt = Number((apiN.meta as Record<string, unknown> | undefined)?.amount ?? 0);
                            type = amt < 0 ? 'error' : 'success';
                          }

                          newNotifications.push({
                              id: apiN.id,
                              type: type,
                              title: apiN.title,
                              message: apiN.body,
                              timestamp: new Date(apiN.created_at).getTime(),
                              read: apiN.is_read,
                              metadata: apiN.meta
                          });
                          changed = true;
                      } else {
                          // Sync read status
                          if (apiN.is_read && !exists.read) {
                              exists.read = true;
                              changed = true;
                          }
                      }
                  });
                  
                  // Sort by timestamp desc, cap at 50
                  newNotifications.sort((a, b) => b.timestamp - a.timestamp);
                  if (newNotifications.length > 50) newNotifications.length = 50;

                  return changed ? { notifications: newNotifications } : state;
              });
          } catch (e) {
              console.error("Failed to sync notifications", e);
          }
      },

      markAsRead: (id) => {
        // Optimistic update
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        }));
        
        // Sync with backend if it's a backend notification (UUID check roughly)
        if (id.length > 30) { // UUID length is 36
             markNotificationRead(id).catch(console.error);
        }
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
        }));
        markAllNotificationsRead().catch(console.error);
      },

      clearAll: () => {
        set({ notifications: [] });
      },

      removeNotification: (id) => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      unreadCount: () => {
        return get().notifications.filter((n) => !n.read).length;
      },
    }),
    {
      name: 'notification-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
